import {Task} from "./views/task";
import {ListType} from "./index";
import {SubscribesSelect} from "./views/subscribes-select";
import {Api, Method} from "../api/api";
import {showToast} from "../toast";
import {getCurrentUser} from "../utils/common";
import {EventType} from "../events";
import {ActivityType, NO_TIME_LIMIT_TEXT} from "../utils/consts";
import dayjs from "dayjs";
import {PresetDateValue} from "../deadline";
import {BurningTaskList} from "./views/burning-task-list";
import {CurrentTaskList} from "./views/current-task-list";
import {FlatpickrFn} from "flatpickr/dist/types/instance";
import type Store from "./api/store";
import {OneTask} from "./views/one-task";
import {SubTask} from "./views/sub-task";
import {AbstractItem} from "./views/abstract-task";
import {TodayTask} from "./views/today-task";
import {FavoriteTask} from "./views/favorite-task";
import {AllTask} from "./views/all-task";
import {QueueTask} from "./views/queue-task";

export class Presenter {
  private eventEmitter = window.taskEmitter;
  private api: Api;

  flatpickr: FlatpickrFn;
  tasksStore: Store;
  burningView: BurningTaskList | null;
  currentView: CurrentTaskList | null;

  constructor(tasksStore: Store, api: Api, flatpickr: FlatpickrFn, burningView: BurningTaskList | null,
    currentView: CurrentTaskList | null) {
    this.api = api;

    this.flatpickr = flatpickr;

    this.tasksStore = tasksStore;
    this.burningView = burningView;
    this.currentView = currentView;
  }

  init() {
    const burningTasksViews: Record<string, Task> = this.tasksStore.getItems(ListType.BURNING);
    const currentTasksViews: Record<string, Task> = this.tasksStore.getItems(ListType.CURRENT);
    const todayTasksViews: Record<string, TodayTask> = this.tasksStore.getItems(ListType.TODAY);
    const favoriteTasksViews: Record<string, FavoriteTask> = this.tasksStore.getItems(ListType.FAVORITE);
    const allTasksViews: Record<string, AllTask> = this.tasksStore.getItems(ListType.ALL);
    const queueTasksViews: Record<string, QueueTask> = this.tasksStore.getItems(ListType.QUEUE);
    const oneTaskView: Record<string, OneTask> = this.tasksStore.getItems(ListType.ONE_TASK);
    const subTaskView: Record<string, SubTask> = this.tasksStore.getItems(ListType.SUB_TASK);

    for (const key in burningTasksViews) {
      this.addViewWatchers(burningTasksViews[key], key);
    }

    for (const key in currentTasksViews) {
      this.addViewWatchers(currentTasksViews[key], key);
    }

    for (const key in todayTasksViews) {
      this.addViewWatchers(todayTasksViews[key], key);
    }

    for (const key in favoriteTasksViews) {
      this.addViewWatchers(favoriteTasksViews[key], key);
    }

    for (const key in allTasksViews) {
      this.addViewWatchers(allTasksViews[key], key);
    }

    for (const key in queueTasksViews) {
      this.addViewWatchers(queueTasksViews[key], key);
    }

    for (const key in oneTaskView) {
      this.addViewWatchers(oneTaskView[key], key);
    }

    for (const key in subTaskView) {
      this.addViewWatchers(subTaskView[key], key);
    }

    this.eventEmitter.on(`task/initAddedQueue`, (id: string, view: QueueTask) => {
      this.addViewWatchers(view, id);
    });
  }

  private getCurrentView(view, taskType, id) {
    const isBurning = id in this.tasksStore.getItems(ListType.BURNING);
    const isAll = id in this.tasksStore.getItems(ListType.ALL);
    const isQueue = id in this.tasksStore.getItems(ListType.QUEUE);
    const isOneTaskPage = id in this.tasksStore.getItems(ListType.ONE_TASK);

    if (taskType === ListType.TODAY || taskType === ListType.FAVORITE) {
      if (isAll) {
        return this.tasksStore.getItem(id, ListType.ALL);
      }

      if (isQueue) {
        return this.tasksStore.getItem(id, ListType.QUEUE);
      }

      if (isOneTaskPage) {
        return this.tasksStore.getItem(id, ListType.ONE_TASK);
      }

      if (isBurning) {
        return this.tasksStore.getItem(id, ListType.BURNING);
      } else {
        return this.tasksStore.getItem(id, ListType.CURRENT);
      }
    } else if ((taskType === ListType.FAVORITE && isAll) || (taskType === ListType.FAVORITE && isOneTaskPage)) {
      return view;
    } else {
      return view;
    }
  }

  private appendElement(element, days, id) {
    const isBurning = id in this.tasksStore.getItems(ListType.BURNING);

    if (days >= 0) {
      if (isBurning) {
        this.currentView?.addElement(element);
      }
    } else {
      if (!isBurning) {
        this.burningView?.addElement(element);
      }
    }
  }

  private removeTaskView(days, id, isBurning, isCurrent) {
    if (days >= 0) {
      if (isBurning) {
        this.tasksStore.removeItem(id, ListType.BURNING);
      }
    } else {
      if (isCurrent) {
        this.tasksStore.removeItem(id, ListType.CURRENT);
      }
    }
  }

  private addNewTaskView(days, view, element, isBurning, isCurrent) {
    if (days >= 0) {
      if (isBurning) {
        this.tasksStore.setItem(element.dataset.id, view, ListType.CURRENT);
      }
    } else {
      if (isCurrent) {
        this.tasksStore.setItem(element.dataset.id, view, ListType.BURNING);
      }
    }
  }

  private updatePriority(id, priority, type) {
    this.tasksStore.getItem(id, type)
      .setPriority(priority);
  }

  private updateAllTasksPriority(id, priority) {
    const isBurning = id in this.tasksStore.getItems(ListType.BURNING);
    const isCurrent = id in this.tasksStore.getItems(ListType.CURRENT);
    const isToday = id in this.tasksStore.getItems(ListType.TODAY);
    const isFavorite = id in this.tasksStore.getItems(ListType.FAVORITE);
    const isAll = id in this.tasksStore.getItems(ListType.ALL);
    const isQueue = id in this.tasksStore.getItems(ListType.QUEUE);
    const isOneTaskPage = id in this.tasksStore.getItems(ListType.ONE_TASK);
    const isSubTask = id in this.tasksStore.getItems(ListType.SUB_TASK);

    if (isBurning) {
      this.updatePriority(id, priority, ListType.BURNING);
    }

    if (isCurrent) {
      this.updatePriority(id, priority, ListType.CURRENT);
    }

    if (isToday) {
      this.updatePriority(id, priority, ListType.TODAY);
    }

    if (isFavorite) {
      this.updatePriority(id, priority, ListType.FAVORITE);
    }

    if (isAll) {
      this.updatePriority(id, priority, ListType.ALL);
    }

    if (isQueue) {
      this.updatePriority(id, priority, ListType.QUEUE);
    }

    if (isOneTaskPage) {
      this.updatePriority(id, priority, ListType.ONE_TASK);
    }

    if (isSubTask) {
      this.updatePriority(id, priority, ListType.SUB_TASK);
    }
  }

  private async changeTaskCloseStatus(id: string, closeStatus: boolean) {
    // const {code, answer} = await this.api.customQuery(`/data/close-task.json?id=${id}&closingStatus=${closeStatus}`, {});
    const {code, answer} = await this.api.customQuery(`/task/close-task?id=${id}&closingStatus=${closeStatus}`, {});

    if (code === 200) {
      return 200;
    } else {
      return 500;
    }
  }

  private addViewWatchers(view: AbstractItem, key: string) {
    const taskType = view.getType();

    const watchPriorityInputChange = (evt) => {
      const priority = +evt.currentTarget.value;
      const currentPriority = +view.getCurrentPriorityValue();

      if (currentPriority !== priority) {
        this.api
          .updatePriority(key, priority)
          .then((data) => {
            if (data === 200) {
              this.updateAllTasksPriority(key, priority);
            }
          })
          .catch((error) => console.log(error));
      }
    };

    const watchDeadLineInfoClick = (evt) => {
      if (taskType === ListType.ONE_TASK && !evt.target.classList.contains(`js-deadline-start-date`)) {
        if ((view as OneTask).getChangeDayModalElement()
          .classList
          .contains(`modal--show`)) {
          (view as OneTask).setActivityChangeDayModal(ActivityType.HIDE);
        } else {
          (view as OneTask).setActivityChangeDayModal(ActivityType.SHOW);
        }
      } else {
        view.openDatePicker();
      }
    };

    const watchChangeDayModalClick = (evt) => {
      evt.stopPropagation();
      const target = evt.target.textContent.trim();

      let date;
      switch (target) {
        case PresetDateValue.TODAY:
          date = new Date();
          break;
        case PresetDateValue.TOMORROW:
          date = dayjs()
            .add(1, `day`)
            .toDate();
          break;
        case PresetDateValue.MONDAY:
          date = dayjs()
            .day(8)
            .toDate();
          break;
        case PresetDateValue.NEXT_MONTH:
          let newDate = dayjs()
            .month(dayjs()
              .month() + 1)
            .date(1);
          if (newDate.day() !== 1) {
            const shift = newDate.day() > 1 ? 8 - newDate.day() : 1;
            newDate = newDate.day(newDate.day() + shift);
          }
          date = newDate.toDate();
          break;
        case PresetDateValue.NO_TIME_LIMIT:
          date = null;
          break;
      }

      (view as OneTask).setActivityChangeDayModal(ActivityType.HIDE);
      (view as OneTask).setFlatpickrDate(date);
    };

    const watchDateInputChange = (date: string, formattedDate) => {
      const isBurning = key in this.tasksStore.getItems(ListType.BURNING);
      const isCurrent = key in this.tasksStore.getItems(ListType.CURRENT);
      const isToday = key in this.tasksStore.getItems(ListType.TODAY);
      const isFavorite = key in this.tasksStore.getItems(ListType.FAVORITE);
      const isAll = key in this.tasksStore.getItems(ListType.ALL);
      const isQueue = key in this.tasksStore.getItems(ListType.QUEUE);
      const isOneTaskPage = key in this.tasksStore.getItems(ListType.ONE_TASK);
      const isSubTask = key in this.tasksStore.getItems(ListType.SUB_TASK);

      if (view.getCurrentDeadlineValue() !== date) {
        this.api.updateDeadline(key, date)
          .then((data) => {
            if (data === 200) {
              const oldDate = view.getCurrentDeadlineValue();

              view.setCurrentDeadlineValue(date);

              const days = date ?
                Math.ceil((new Date(`${date}`).getTime() - new Date().getTime()) / 1000 / 60 / 60 / 24) :
                NO_TIME_LIMIT_TEXT;

              const isReRenderItem = (days >= 0 && isBurning) || (days < 0 && isCurrent);

              const changedView = this.getCurrentView(view, taskType, key);
              changedView.setDateToDeadline(days);

              if (
                taskType === ListType.TODAY ||
                taskType === ListType.FAVORITE ||
                taskType === ListType.ALL ||
                taskType === ListType.QUEUE ||
                taskType === ListType.ONE_TASK ||
                taskType === ListType.SUB_TASK
              ) {
                view.setDateToDeadline(days);
              }

              if (isReRenderItem && !isAll && !isQueue && !isOneTaskPage) {
                changedView.destroyDatePicker();

                const element = Presenter.getCurrentHtmlElement(changedView, view, taskType);

                this.appendElement(element, days, key);

                changedView.removeElement();

                this.burningView?.updateItems();
                this.currentView?.updateItems();

                this.removeTaskView(days, key, isBurning, isCurrent);

                const taskView = new Task(element, this.flatpickr, taskType);
                taskView.init();

                this.addNewTaskView(days, taskView, element, isBurning, isCurrent);

                this.addViewWatchers(taskView, key);
              }

              // TODO: рефакторинг вынести в отдельный метод
              if (isToday) {
                const todayTaskView = this.tasksStore.getItem(key, ListType.TODAY);
                todayTaskView.setDateToDeadline(days);
                todayTaskView.setCurrentDeadlineValue(date);

                if (days >= 0) {
                  todayTaskView.setDeadlineStatusClass(`current`);
                } else {
                  todayTaskView.setDeadlineStatusClass(`burn`);
                }
              }
              // TODO: рефакторинг вынести в отдельный метод
              if (isFavorite) {
                const favoriteTaskView = this.tasksStore.getItem(key, ListType.FAVORITE);

                if (favoriteTaskView.openElement) {
                  favoriteTaskView.setDateToDeadline(days);
                  favoriteTaskView.setCurrentDeadlineValue(date);

                  if (days >= 0) {
                    favoriteTaskView.setDeadlineStatusClass(`current`);
                  } else {
                    favoriteTaskView.setDeadlineStatusClass(`burn`);
                  }
                }
              }

              if (isSubTask) {
                const subtaskTaskView = this.tasksStore.getItem(key, ListType.SUB_TASK);

                if (subtaskTaskView.openElement) {
                  subtaskTaskView.setDateToDeadline(days);
                  subtaskTaskView.setCurrentDeadlineValue(date);

                  if (days >= 0) {
                    subtaskTaskView.setDeadlineStatusClass(`current`);
                  } else {
                    subtaskTaskView.setDeadlineStatusClass(`burn`);
                  }
                }
              }

              if (isAll) {
                const allTaskView = this.tasksStore.getItem(key, ListType.ALL);
                allTaskView.setDateToDeadline(days);
                allTaskView.setCurrentDeadlineValue(date);

                if (days >= 0) {
                  allTaskView.setDeadlineStatusClass(`current`);
                } else {
                  allTaskView.setDeadlineStatusClass(`burn`);
                }
              }

              if (isQueue) {
                const queueTaskView = this.tasksStore.getItem(key, ListType.QUEUE);
                queueTaskView.setDateToDeadline(days);
                queueTaskView.setCurrentDeadlineValue(date);

                if (days >= 0) {
                  queueTaskView.setDeadlineStatusClass(`current`);
                } else {
                  queueTaskView.setDeadlineStatusClass(`burn`);
                }
              }

              if (isOneTaskPage) {
                const oneTaskPageView = this.tasksStore.getItem(key, ListType.ONE_TASK);

                this.eventEmitter.emit(`events.render`, EventType.DEADLINE, {
                  user: getCurrentUser(),
                  date: {
                    new: date ? date : NO_TIME_LIMIT_TEXT,
                    old: oldDate,
                  },
                });

                oneTaskPageView.setDateToDeadline(days);
                oneTaskPageView.setDeadlineDate(formattedDate);
                oneTaskPageView.setCurrentDeadlineValue(date);

                if (oneTaskPageView.isNotDeadline()) {
                  oneTaskPageView.removeDeadlineNotEndClass();
                  // oneTaskPageView.removeDeadlineStartEventListener();
                }

                if (days === NO_TIME_LIMIT_TEXT) {
                  oneTaskPageView.addDeadlineNotEndClass();
                }

                if (days >= 0 || days === NO_TIME_LIMIT_TEXT) {
                  oneTaskPageView.setDeadlineStatusClass(`current`);
                } else {
                  oneTaskPageView.setDeadlineStatusClass(`burn`);
                }
              }
            }
          });
      }
    };

    view.setOpenDeadlineInfoClickHandler(watchDeadLineInfoClick);
    view.setDateInputChangeHandler(watchDateInputChange);
    view.setPriorityInputChangeHandler(watchPriorityInputChange);

    if (taskType === ListType.ONE_TASK) {
      const subscribeSelect = new SubscribesSelect(view.getItem());

      const watchOpenSubsListButtonClick = () => subscribeSelect.toggleActivityModalSubsStatus();
      const watchSubscribersListClick = async (evt) => {
        const target = evt.target;

        if (target.classList.contains(`modal__item`) || target.closest(`.modal__item`)) {
          const element = target.closest(`.modal__item`) ? target.closest(`.modal__item`) : target;

          subscribeSelect.toggleActivitySubscribeElement(element);
          subscribeSelect.changeSubscribers(element);
          subscribeSelect.renderSubscriber(element);

          // const {code, answer} = await this.api.customQuery(`/data/subscribers.json`, {
          const {code, answer} = await this.api.customQuery(`/task/edit-sub`, {
            method: Method.POST,
            query: JSON.stringify({
              id: view.getId(),
              subscribers: subscribeSelect.currentSubscribers,
            }),
            headers: {
              "Content-Type": "application/json; charset=UTF-8",
            },
          });

          if (code !== 200) {
            showToast(`При обновлении подписчиков, что-то пошло не так. Обновите страницу.`);
          }
        }
      };

      const updateSubscribersListFromServer = (subscribers) => {
        subscribeSelect.disabledSubscribers(subscribers);
        subscribeSelect.currentSubscribers = subscribers;
      };

      const watchWindowClick = (evt) => {
        const target = evt.target;
        if (
          target.closest(`.js-subscribers-modal`) !== subscribeSelect.getModalElement() &&
          target.closest(`.js-open-subscribers`) !== subscribeSelect.getOpenButtonElement()
        ) {
          subscribeSelect.closeModalSubsStatus();
        }

        if ((view as OneTask).isChangeDayOpen()) {
          (view as OneTask).setActivityChangeDayModal(ActivityType.HIDE);
        }
      };

      const watchCloseTaskClick = async () => {
        if ((view as OneTask).isSentClosingStatus) return;

        (view as OneTask).isSentClosingStatus = true;

        const res = await this.changeTaskCloseStatus(view.getId(), !(view as OneTask).isClosingStatus);

        if (res === 200) {
          (view as OneTask).isClosingStatus = !(view as OneTask).isClosingStatus;
          (view as OneTask).setClosingStatusClass();
          (view as OneTask).setClosingButtonText();
          (view as OneTask).setActivityCloseTaskModal(ActivityType.HIDE);
          (view as OneTask).isCloseModalOpen = !(view as OneTask).isCloseModalOpen;
          this.eventEmitter.emit(`events.render`, EventType.CLOSE, {user: getCurrentUser()});
          this.eventEmitter.emit(`timer.update`);
        }

        (view as OneTask).isSentClosingStatus = false;
      };

      const watchOpenCloseModalButtonClick = async () => {
        if ((view as OneTask).isClosingStatus) {
          const res = await this.changeTaskCloseStatus(view.getId(), !(view as OneTask).isClosingStatus);

          if (res === 200) {
            (view as OneTask).isClosingStatus = !(view as OneTask).isClosingStatus;
            (view as OneTask).setClosingStatusClass();
            (view as OneTask).setClosingButtonText();
            this.eventEmitter.emit(`events.render`, EventType.OPEN, {user: getCurrentUser()});
          }
          return;
        }

        (view as OneTask).setActivityCloseTaskModal(ActivityType.SHOW);
        (view as OneTask).isCloseModalOpen = !(view as OneTask).isCloseModalOpen;
      };

      const watchCloseModalElementClick = async (evt) => {
        const target = evt.target;
        if (
          (!target.classList.contains(`modal--delete`) && !target.closest(`.modal--delete`)) ||
          target.classList.contains(`js-close`)
        ) {
          (view as OneTask).setActivityCloseTaskModal(ActivityType.HIDE);
          (view as OneTask).isCloseModalOpen = !(view as OneTask).isCloseModalOpen;
        }
      };

      (view as OneTask).setOpenCloseModalClickHandler(watchOpenCloseModalButtonClick);
      (view as OneTask).setCloseTaskButtonClickHandler(watchCloseTaskClick);
      (view as OneTask).setChangeDayModalClickHandler(watchChangeDayModalClick);
      (view as OneTask).setDeadLineDateElementClickHandler(watchDeadLineInfoClick);
      (view as OneTask).setCloseModalClickHandler(watchCloseModalElementClick);

      try {
        subscribeSelect.setWindowClickHandler(watchWindowClick);
        subscribeSelect.setOpenSubscribersButtonClickHandler(watchOpenSubsListButtonClick);
        subscribeSelect.setSubscribersListClickHandler(watchSubscribersListClick);
        this.eventEmitter.on(`subscribers.update`, updateSubscribersListFromServer);
      } catch (e) {
        console.log(e);
      }
    }
  }

  static getCurrentHtmlElement(changedView, view, taskType) {
    if (taskType === ListType.TODAY || taskType === ListType.FAVORITE) {
      return changedView.getElement();
    } else {
      return view.getElement();
    }
  }
}
